import React, { useState } from "react"
import clsx from "clsx"
import {
	Button,
	Grid,
	List,
	IconButton,
	Drawer,
	CssBaseline,
	Divider
} from "@material-ui/core"

import {
	Close as CloseIcon
} from "@material-ui/icons"

import {
	MessageCircleIcon,
	PanelRight as MinimizeIcon,
	PanelLeft as MaximizeIcon
} from "lucide-react"

import { isSmallScreen } from "@/utils/checkDevice"
import useStyles from "@/@integrations/Whatsapp/components/Menu/NewSideBar/styles"
import useMenuStyles from "@/pages/Admin/Menu/styles"
import useCustomStyles from "@/styles/custom"

import LetalkLogo from "@/components/SVGComponents/LetalkLogo"
import LetalkVerticalLogo from "@/components/SVGComponents/LetalkVerticalLogo"

import MenuItemWithPopper from "@/components/MenuItemWithPopper"
import InstanceSelectorItem from "@/@integrations/Whatsapp/components/Menu/NewSideBar/InstanceSelectorItem"
import UserProfileItem from "@/@integrations/Whatsapp/components/Menu/NewSideBar/UserProfileItem"
import { useDashboardMenuData } from "@/hooks/useDashboardMenuData"
import { ChannelType } from "@/protocols/channel"

type MenuProps = {
	hiddenTabs: boolean
	openDrawer: boolean
	onClose: () => void
	channelType?: ChannelType
}

const SideBar = ({ hiddenTabs, onClose, channelType }: MenuProps) => {
	const classes = useStyles()
	const menuClasses = useMenuStyles()
	const customClasses = useCustomStyles()

	const [isSidebarOpen, setIsSidebarOpen] = useState(true)
	const [showMinimizeIcon, setShowMinimizeIcon] = useState(true)

	const shouldShowMinimizeIcon = isSidebarOpen && showMinimizeIcon
	const menuData = useDashboardMenuData(channelType, classes, hiddenTabs)

	const {
		defaultMenuCategories,
		bottomMenuCategories,
		hoveredCategory,
		handleEnter,
		handleLeave,
		handleCategoryClick,
		anchorRefs
	} = menuData

	const helpCategory = bottomMenuCategories.find((item) => item.id === "help")
	const accountCategory = bottomMenuCategories.find((item) => item.id === "myaccount")

	const handleMinimizeMenu = () => setIsSidebarOpen(false)
	const handleMaximizeMenu = () => {
		setIsSidebarOpen(true)

		setShowMinimizeIcon(false)

		setTimeout(() => {
			setShowMinimizeIcon(true)
		}, 200)
	}

	const drawerContent = (
		<Grid
			container
			direction="column"
			className={classes.drawerInternalContainer}
		>

			<Grid item className={menuClasses.topInfoContainer}>
				{isSmallScreen && (
					<Grid container justify="space-between" alignItems="center">
						<Grid item xs>
							<LetalkLogo
								isDarkLogo
								className={classes.logo}
							/>
						</Grid>

						<Grid item>
							<IconButton color="inherit" onClick={onClose}>
								<CloseIcon className={menuClasses.closeIcon} />
							</IconButton>
						</Grid>
					</Grid>
				)}
			</Grid>

			<Grid container justify="space-between" alignItems="center" className={classes.logoRow}>
				<Grid item className={isSidebarOpen ? classes.logoContainerDrawer : classes.logoContainerDrawerClosed}>
					{isSidebarOpen ? (
						<LetalkLogo isDarkLogo className={classes.logo} />
					) : (
						<LetalkVerticalLogo isDarkLogo className={classes.verticalLogo} />
					)}
				</Grid>

				<Grid item className={classes.minimizeIconContainer}>
					{ shouldShowMinimizeIcon && (
						<MinimizeIcon className={classes.minimizeIcon} onClick={handleMinimizeMenu} />
					)}
				</Grid>
			</Grid>

			<Grid
				item
				className={clsx(
					isSmallScreen ? classes.listContainerMobile : classes.listContainer,
					customClasses.scrollBar
				)}
			>
				{!isSidebarOpen && (
					<Grid container justifyContent="center" className={classes.maximizeIconWrapper}>
						<MaximizeIcon
							className={classes.minimizeIcon}
							onClick={handleMaximizeMenu}
						/>
					</Grid>
				)}

				<Grid item className={clsx(classes.goToInboxButtonContainer, !isSidebarOpen && classes.goToInboxButtonContainerClosed)}>
					<Button
						className={clsx(classes.goToInboxButton, !isSidebarOpen && classes.goToInboxButtonClosed)}
						onClick={() => window.open("/attendance", "_blank")}
						title="Ir para o inbox"
					>
						<MessageCircleIcon size={20} style={isSidebarOpen ? { marginRight: "8px" } : undefined} />
						{isSidebarOpen && "Ir para o inbox"}
					</Button>
				</Grid>

				<List className={classes.list}>
					{defaultMenuCategories.map((item) => (
						<MenuItemWithPopper
							key={item.id}
							item={item}
							anchorRefs={anchorRefs}
							hoveredCategory={hoveredCategory}
							handleEnter={handleEnter}
							handleLeave={handleLeave}
							handleClick={handleCategoryClick}
							isSidebarOpen={isSidebarOpen}
						/>
					))}
				</List>
			</Grid>

			<Grid item style={{ flexGrow: 1 }} />

			<Grid item>
				<Grid container direction="column" className={classes.bottomContainer}>
					{helpCategory && (
						<Grid item className={classes.bottomFixed}>
							<MenuItemWithPopper
								item={helpCategory}
								anchorRefs={anchorRefs}
								hoveredCategory={hoveredCategory}
								handleEnter={handleEnter}
								handleLeave={handleLeave}
								handleClick={handleCategoryClick}
								isSidebarOpen={isSidebarOpen}
							/>
						</Grid>
					)}

					<Grid item>
						<Divider className={classes.sidebarDivider} />
					</Grid>

					<Grid item className={classes.bottomFixed}>
						<InstanceSelectorItem isSidebarOpen={isSidebarOpen} />
					</Grid>

					{accountCategory && (
						<Grid item className={classes.bottomFixed}>
							<UserProfileItem
								accountMenuCategory={accountCategory}
								anchorRefs={anchorRefs}
								hoveredCategory={hoveredCategory}
								handleEnter={handleEnter}
								handleLeave={handleLeave}
								handleClick={handleCategoryClick}
								isSidebarOpen={isSidebarOpen}
							/>
						</Grid>
					)}
				</Grid>
			</Grid>
		</Grid>
	)

	return (
		<div className={classes.root}>
			<CssBaseline />
			<Drawer
				variant="permanent"
				className={clsx(classes.drawer, {
					[classes.drawerOpen]: isSidebarOpen,
					[classes.drawerClose]: !isSidebarOpen
				})}
				classes={{
					paper: clsx({
						[classes.drawerOpen]: isSidebarOpen,
						[classes.drawerClose]: !isSidebarOpen
					})
				}}
			>
				<div />
				{drawerContent}
			</Drawer>

			<main className={classes.content}>
				<div />
			</main>
		</div>
	)
}

export default SideBar
